/* Mock routes — authentication, account and the developer data controls. */

import type { Session, User } from '@core/types'
import { ApiError } from '../../http'
import { db, resetDb } from '../store'
import { asRecord, readString, type MockRoute } from '../types'

export const accountRoutes: MockRoute[] = [
  {
    method: 'POST',
    pattern: '/auth/login',
    handler: ({ body }) => {
      const email = readString(body, 'email')
      const password = readString(body, 'password')
      const fieldErrors: Record<string, string> = {}
      if (!email) fieldErrors.email = 'Enter the email you registered with.'
      if (!password) fieldErrors.password = 'Enter your password.'
      if (Object.keys(fieldErrors).length > 0) {
        throw new ApiError({
          status: 422,
          code: 'validation_failed',
          message: 'Enter your email and password.',
          fieldErrors,
        })
      }
      if (password !== 'demo1234') {
        throw new ApiError({
          status: 401,
          code: 'invalid_credentials',
          message:
            'That email and password combination was not recognised. Check for typos, or reset the password if you have forgotten it.',
        })
      }
      return {
        token: `demo-session-${Date.now()}`,
        user: { ...db.user, email: email as string },
      } satisfies Session
    },
  },
  {
    method: 'POST',
    pattern: '/auth/register',
    handler: ({ body }) => {
      const displayName = readString(body, 'displayName')
      const email = readString(body, 'email')
      const password = readString(body, 'password')
      const governorate = readString(body, 'governorate')
      const district = readString(body, 'district')

      const fieldErrors: Record<string, string> = {}
      if (!displayName || displayName.trim().length < 3) {
        fieldErrors.displayName = 'Enter your full name — at least 3 characters.'
      }
      if (!email) fieldErrors.email = 'Enter your email address.'
      if (!password || password.length < 8) {
        fieldErrors.password = 'Choose a password of at least 8 characters.'
      }
      if (!governorate) fieldErrors.governorate = 'Select your governorate.'
      if (!district) fieldErrors.district = 'Add your district so advice can be location-specific.'
      if (Object.keys(fieldErrors).length > 0) {
        throw new ApiError({
          status: 422,
          code: 'validation_failed',
          message: 'The account could not be created yet — check the highlighted fields.',
          fieldErrors,
        })
      }

      db.user = {
        ...db.user,
        id: `us-${Date.now()}`,
        displayName: displayName as string,
        email: email as string,
        governorate: governorate as User['governorate'],
        district: district as string,
        plantsCount: 0,
        createdAt: new Date().toISOString(),
      }
      return { token: `demo-session-${Date.now()}`, user: db.user } satisfies Session
    },
  },
  {
    method: 'POST',
    pattern: '/auth/logout',
    handler: () => ({ ok: true }),
  },
  {
    method: 'GET',
    pattern: '/me',
    handler: () => ({ ...db.user, plantsCount: db.plants.length }) satisfies User,
  },
  {
    method: 'PATCH',
    pattern: '/me',
    handler: ({ body }) => {
      const record = asRecord(body)
      const displayName = readString(body, 'displayName')
      const phone = readString(body, 'phone')
      const governorate = readString(body, 'governorate')
      const district = readString(body, 'district')
      const language = readString(body, 'language')

      if (displayName) db.user.displayName = displayName
      if (phone) db.user.phone = phone
      if (governorate) db.user.governorate = governorate as User['governorate']
      if (district) db.user.district = district
      if (language === 'ar' || language === 'en') db.user.language = language

      const prefs = asRecord(record.notificationPrefs)
      for (const key of Object.keys(db.user.notificationPrefs) as Array<
        keyof User['notificationPrefs']
      >) {
        const value = prefs[key]
        if (typeof value === 'boolean') db.user.notificationPrefs[key] = value
      }

      return { ...db.user, plantsCount: db.plants.length } satisfies User
    },
  },
  {
    method: 'DELETE',
    pattern: '/me',
    handler: ({ body }) => {
      const confirmation = readString(body, 'confirmation')
      if (confirmation !== 'DELETE') {
        throw new ApiError({
          status: 422,
          code: 'confirmation_required',
          message: 'Type DELETE to confirm. This request is reviewed by an administrator.',
        })
      }
      return {
        requestedAt: new Date().toISOString(),
        note: 'Account deletion is processed within 30 days. Campaign records that reference your planted trees are anonymised rather than deleted, so survival data stays intact.',
      }
    },
  },
  {
    method: 'POST',
    pattern: '/dev/reset-data',
    handler: () => {
      resetDb()
      return { ok: true, resetAt: new Date().toISOString() }
    },
  },
]
